import React, { ReactElement, useEffect, useState } from "react";
import { IDimensions } from "../../../../components/charts/chartUtils";
import { IAcquisitionChartConfig, IToolbox } from "../../types";
import { AxisTypes, IPoint } from "../../../../components/charts/types";
import { IDictionary } from "../../../../types";
import * as d3 from "d3";
import { useTheme } from "@mui/material";

interface IProps {
  lineID: string;
  dimensions: IDimensions;
  toolbox: IToolbox;
  onZoomArea: (from: number, to: number, y0: number, y1: number) => void;
  chartConfig: IAcquisitionChartConfig;
  xScale: d3.ScaleLinear<number, number>;
  yScales: IDictionary<{ axis: d3.ScaleLinear<number, number>, config: AxisTypes, index: number, }>;
  stackedAxes: boolean;
}

interface ICursorData {
  x: number;
  y: number;
  x0: number;
  y0: number;
  w: number;
  h: number;
  value: string;
  drag: boolean;
  series: Record<number, number>;
}

const defaultCursorData: ICursorData = { x: -1, drag: false, value: "", series: {}, y: 0, w: 0, h: 0, x0: 0, y0: 0 };

const ToolboxArea = ({ lineID, dimensions, toolbox, onZoomArea, chartConfig, yScales, xScale, stackedAxes }: IProps): ReactElement => {
  const [ cursorData, setCursorData ] = useState<ICursorData>(defaultCursorData);
  const [ unitLen, setUnitLen ] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    setCursorData(defaultCursorData);

    let length = 0;
    chartConfig.series.forEach(s => {
      if (s.unit) {
        length = Math.max(length, s.unit.length * 7 + 10);
      }
    });

    setUnitLen(length);
  }, [ chartConfig ]);


  const getSeriesValues = (value: number): Record<number, number> => {
    const result: Record<number, number> = {};

    for (let si = 0; si < chartConfig.series.length; si++){
      const series = chartConfig.series[si];
      const points = series.value as IPoint[];

      if(points.length > 0) {
        const min = points[0].x as number;
        const max = points[points.length - 1].x as number;

        if (value >= min && value <= max){
          const vp = value - min;
          result[si] = Math.round(vp * points.length / (max - min));
        }
      }
    }

    return result;
  };

  const onMouseDown = (event: React.MouseEvent<any>) => {
    if(toolbox.cursor) {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const value = event.clientX - rect.x;
      const invertValue = xScale.invert(value);

      setCursorData({
        ...cursorData,
        x: value,
        value: chartConfig.xAxis.formatTick ? chartConfig.xAxis.formatTick(invertValue, 0) : "---",
        series: getSeriesValues(invertValue),
        drag: true
      });
    } else if(toolbox.zoom) {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const valueX = event.clientX - rect.x;
      const valueY = event.clientY - rect.y;

      setCursorData({
        ...defaultCursorData,
        x: valueX,
        y: valueY,
        x0: valueX,
        y0: valueY,
        drag: true
      });
    }
  };

  const onMouseMove = (event: React.MouseEvent<SVGGElement>) => {
    if(toolbox.cursor && cursorData.drag) {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const value = event.clientX - rect.x;
      const invertValue = xScale.invert(value);

      if(value !== cursorData.x) {
        setCursorData({
          ...cursorData,
          x: value,
          value: chartConfig.xAxis.formatTick ? chartConfig.xAxis.formatTick(invertValue, 0) : "---",
          series: getSeriesValues(invertValue)
        });
      }
    } else if (toolbox.zoom && cursorData.drag) {
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const valueX = event.clientX - rect.x;
      const valueY = event.clientY - rect.y;

      setCursorData({
        ...cursorData,
        x: valueX < cursorData.x0 ? valueX : cursorData.x0,
        y: valueY < cursorData.y0 ? valueY : cursorData.y0,
        w: Math.abs(valueX - cursorData.x0),
        h: Math.abs(valueY - cursorData.y0),
        drag: true
      });
    }
  };

  const onMouseUp = (event: React.MouseEvent) => {
    if (cursorData.drag) {
      event.stopPropagation();
      setCursorData({ ...cursorData, drag: false });

      if (!toolbox.cursor && toolbox.zoom){
        const from = xScale.invert(cursorData.x);
        const to = xScale.invert(cursorData.x + cursorData.w);
        const min10Secs = 10;

        if(to - from >= min10Secs) {
          const y0 = (dimensions.boundedHeight - (cursorData.y + cursorData.h)) / dimensions.boundedHeight;
          const y1 = 1 - cursorData.y / dimensions.boundedHeight;

          onZoomArea(from, to, y0, y1);
        }
      }
    }
  };

  const entries = Object.entries(cursorData.series);

  let cursorXPos =  cursorData.x + 10;
  if (cursorData.x + 400 > dimensions.boundedWidth) {
    cursorXPos = cursorData.x - 390 > 0 ? cursorData.x - 390 : cursorData.x - 195;
  }

  let boundedHeight = dimensions.boundedHeight;
  if (chartConfig.yAxis.length > 0 && stackedAxes) {
    boundedHeight = (dimensions.boundedHeight - (chartConfig.yAxis.length - 1) * 15) / chartConfig.yAxis.length;
  }


  return (
    <g>
      {toolbox.cursor && cursorData.x >= 0 &&
        <g>
          <line x1={cursorData.x} x2={cursorData.x} y1={0} y2={dimensions.boundedHeight + 5} style={{ cursor: "pointer" }}
            strokeWidth={2} stroke={theme.palette.text.primary} strokeOpacity={.5} strokeDasharray="4 4"/>

          <text x={cursorData.x} y={dimensions.boundedHeight + 17} textAnchor={"middle"} fontWeight={300}
            stroke={theme.palette.text.primary} strokeOpacity={.5}>{cursorData.value}</text>

          {entries.map(([k, v], i) => {
            const series = chartConfig?.series[parseInt(k, 10)];
            const points = series?.value as IPoint[];

            if (points.length <= v) {
              return null;
            }

            const point = points[v];
            const x = xScale(point.x as number);
            const y = yScales[series?.yAxisName].axis(point.y);
            const offset = stackedAxes ? (boundedHeight + 15) * yScales[series?.yAxisName].index : 0;
            const range = yScales[series?.yAxisName].axis.range();

            if (y < range[1] || y > range[0]) {
              return null;
            }

            return (
              <circle key={`${lineID}-cursor-s-${i}`} cx={x} cy={y + offset} r="3" fill="white" stroke={series.color} strokeWidth={2}/>
            );
          })
          }

          <g transform={`translate(${cursorXPos}, 10)`}>
            <rect width={380} height={35 + entries.length * 16} fill={"#616161"} fillOpacity={".9"} rx={3} ry={3}
              stroke="#dadde9" strokeWidth={1}/>

            <text x={190} y={17} textAnchor={"middle"} fontWeight={300} stroke="white">{cursorData.value}</text>

            {entries.map(([k, v], i) => {
              const series = chartConfig?.series[parseInt(k, 10)];
              const points = series?.value as IPoint[];

              if (points.length <= v) {
                return null;
              }

              const point = points[v];
              const yConfig = yScales[series?.yAxisName].config;

              return (
                <g key={`${lineID}-tooltip-s-${i}`}>
                  <text x={5} y={40 + i * 16} textAnchor={"start"} fontWeight={200}
                    stroke={series?.color}>{series?.name}</text>
                  <text x={375 - unitLen} y={40 + i * 16} textAnchor={"end"} fontWeight={200} stroke={"white"}>{yConfig.formatTick ? yConfig.formatTick(point.y, 0) : point.y}</text>
                  {series.unit && <text x={375} y={40 + i * 16} textAnchor={"end"} fontWeight={200} fontSize={".7em"} stroke={"white"}>{`[ ${series.unit} ]`}</text>}
                </g>
              );
            })
            }
          </g>
        </g>
      }

      { toolbox.zoom && cursorData.w > 10 && cursorData.h > 10  &&
        <rect x={cursorData.x} y={cursorData.y} width={cursorData.w} height={cursorData.h}
          fill="white" fillOpacity={.5} strokeWidth={2} stroke={"white"} strokeDasharray="4 4"/>
      }

      <rect
        width={dimensions.boundedWidth}
        height={dimensions.boundedHeight}
        fill="transparent"
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}/>
    </g>
  );

} ;

export default ToolboxArea;
